import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DemographicInfo } from 'src/app/people/models/demographicInfo';
import { FormFieldName } from 'src/app/shared/models/application/formFieldName';
import { State } from 'src/app/shared/models/state';
import { Country } from 'src/app/shared/models/country';
import { LookupService, StateCountryLookupService } from 'src/app/shared/services/lookup.service';
import { NotEmptyValidator } from 'src/app/shared/validators/not-empty.validator';
import { IAddressModel } from 'src/app/people/models/addressModel';
import {
  FieldValidatorTracker,
  getAllControlErrors,
} from 'src/app/shared/models/form/field-validator-tracker';
import { Injectable } from '@angular/core';
import { IAddressValidationResult } from './address.service';

interface AddressForm
  extends FormGroup<{
    streetAddress1: FormControl<string>;
    streetAddress2: FormControl<string>;
    city: FormControl<string>;
    stateOrRegion: FormControl<string>;
    postalCode: FormControl<string>;
    country: FormControl<string>;
    isShared: FormControl<boolean>;
  }> {}

@Injectable({
  providedIn: 'root',
})
export class AddressHelperFactory {
  constructor(private formBuilder: FormBuilder, private lookupService: LookupService) {}

  buildSync = (
    address: DemographicInfo,
    addressValidationCacheKey: string,
    isRequired: Partial<Record<FormFieldName, boolean>>,
    isHidden: Partial<Record<FormFieldName, boolean>>,
    isSharedLabel: string = '',
    stateCountryLookupService: StateCountryLookupService = null
  ): AddressHelper =>
    new AddressHelper(
      this.formBuilder,
      addressValidationCacheKey,
      stateCountryLookupService,
      address,
      isSharedLabel ? isSharedLabel : 'Permission to share',
      isRequired,
      isHidden
    );

  buildAddressHelper = async (
    address: DemographicInfo,
    addressValidationCacheKey: string,
    isRequired: Partial<Record<FormFieldName, boolean>> = {},
    isHidden: Partial<Record<FormFieldName, boolean>> = {},
    isSharedLabel: string = ''
  ): Promise<AddressHelper> =>
    this.buildSync(
      address,
      addressValidationCacheKey,
      isRequired,
      isHidden,
      isSharedLabel,
      await this.lookupService.getStateCountryLookup()
    );
}

//not an angular service
export class AddressHelper {
  get regionVisible(): boolean {
    return this.isHidden.StateOrProvince ? false : hasRegion(this.getCountry(), this.getState());
  }

  hasAddress = false;
  isInvalid = false;

  public addressFormGroup: AddressForm;

  get valid(): boolean {
    return this.addressFormGroup.valid;
  }
  get countries() {
    return this.stateCountryLookupService.countries;
  }

  constructor(
    formBuilder: FormBuilder,
    public addressValidationCacheKey: string,
    private stateCountryLookupService: StateCountryLookupService,
    /** address might be null and never get set if validateAddress: false
     * this.getDemographicInfoFromForm() or this.getModelFromForm() is safer across use cases*/
    public address: DemographicInfo,
    public isSharedLabel: string,
    public isRequired: Partial<Record<FormFieldName, boolean>> = {},
    public isHidden: Partial<Record<FormFieldName, boolean>> = {}
  ) {
    const { stateOrRegion, countryCode, state, country } = this.getCountryCodeAndStateRegion(
      this.address
    );
    if (this.address?.hasLocation()) {
      this.hasAddress = true;
    }
    this.addressFormGroup = formBuilder.group({
      streetAddress1: [
        this.address?.streetAddress1,
        this.isRequired.Address1 ? [NotEmptyValidator('Street Address is required')] : [],
      ],
      streetAddress2: this.address?.streetAddress2,
      city: [
        this.address?.city,
        this.isRequired.City ? [NotEmptyValidator('City is required')] : [],
      ],
      stateOrRegion: [
        stateOrRegion,
        this.isRequired.StateOrProvince ? [getStateValidator(country, state)] : [],
      ],
      postalCode: [
        this.address?.postalCode,
        this.isRequired.PostalCode ? [NotEmptyValidator('Postal Code is required')] : [],
      ],
      country: [
        countryCode,
        this.isRequired.Country ? [NotEmptyValidator('Country is required')] : [],
      ],
      isShared: !!this.address?.isShared,
    });
  }

  getLabel = () => (this.isCanada() ? 'Province' : 'State');

  /** update the address field with whatever is in the Form. The read-only component uses that field so be sure it displays correctly.
  If validationResult not passed it, it keeps the original validation result? */
  copyFormToModel = (validationResult: IAddressValidationResult = null): IAddressModel => {
    const model = this.getModelFromForm();
    //copying form to model
    this.address = this.buildDemographicInfo(model).updateValidationStatus(
      validationResult?.status,
      validationResult?.message
    );
    this.hasAddress = this.address.hasLocation();
    return model;
  };

  updateDemographicInfo(demographicInfo: DemographicInfo): IAddressModel {
    this.hasAddress = demographicInfo?.hasLocation() ? true : false;
    //note: it is possible demographicInfo is empty for first load of anonymous form
    this.address = demographicInfo;
    const { stateOrRegion, countryCode } = this.getCountryCodeAndStateRegion(demographicInfo);
    const isShared = this.addressFormGroup.value.isShared;
    this.addressFormGroup.setValue({
      streetAddress1: demographicInfo.streetAddress1 || '',
      streetAddress2: demographicInfo.streetAddress2 || '',
      city: demographicInfo.city || '',
      postalCode: demographicInfo.postalCode || '',
      isShared: isShared,
      stateOrRegion: stateOrRegion,
      country: countryCode,
    });

    return this.getModelFromForm();
  }

  getStatesOrProvinces = () =>
    this.isCanada()
      ? this.stateCountryLookupService.provinces
      : this.stateCountryLookupService.states;

  getModelFromForm = (): IAddressModel => ({
    isShared: this.addressFormGroup.value.isShared,
    postalCode: this.addressFormGroup.value.postalCode,
    city: this.addressFormGroup.value.city,
    streetAddress1: this.addressFormGroup.value.streetAddress1,
    streetAddress2: this.addressFormGroup.value.streetAddress2,
    region: this.addressFormGroup.value.stateOrRegion,
    stateId: this.getState()?.id,
    countryId: this.getCountry()?.id,
  });

  getDemographicInfoFromForm = (): DemographicInfo =>
    this.stateCountryLookupService.buildDemographicInfo(this.getModelFromForm());

  buildDemographicInfo = (address: IAddressModel): DemographicInfo =>
    this.stateCountryLookupService.buildDemographicInfo(address);

  countryChanged() {
    //reset region to avoid old value being persisted after country change
    if (this.shouldClearStateOrRegion()) {
      this.addressFormGroup.controls.stateOrRegion.setValue(null);
    }

    if (this.isRequired.StateOrProvince) {
      this.setStateOrRegionValidators(this.getCountry(), this.getState());
    }
  }

  getInvalidFields = (): FieldValidatorTracker[] => {
    const errors = Array.from(this.yieldInvalidFields()).flat();
    this.isInvalid = !!errors.length;
    return errors;
  };

  private *yieldInvalidFields(): Generator<FieldValidatorTracker[]> {
    for (const fieldName in this.addressFormGroup.controls) {
      const control = this.addressFormGroup.controls[fieldName];
      yield getAllControlErrors(null, fieldName, control);
    }
  }

  private getState(): State {
    return this.stateCountryLookupService.getStateByCode(this.addressFormGroup.value.stateOrRegion);
  }

  private getCountryCodeAndStateRegion(demographicInfo: DemographicInfo): {
    countryCode: string;
    stateOrRegion: string;
    state?: State;
    country?: Country;
  } {
    let countryCode = demographicInfo?.country?.code;
    let country: Country;
    if (countryCode) {
      country = this.stateCountryLookupService.countries.find(
        (c) => c.code === demographicInfo.country.code
      );
    } else if (demographicInfo?.country?.id) {
      //no code set, but has id
      country = this.stateCountryLookupService.countries.find(
        (c) => c.id === demographicInfo.country.id
      );
      countryCode = country?.code;
    } else {
      country = null;
    }
    const stateOrRegion = demographicInfo?.state?.code ?? demographicInfo?.region ?? '';
    let state = null;
    if (!countryCode) {
      if (!stateOrRegion) {
        //errors on form fields if undefined. So set to empty string
        return {
          countryCode: '',
          stateOrRegion: '',
        };
      }
      state = this.stateCountryLookupService.getStateByCode(stateOrRegion);

      //if state set, but not country, see if the state is US or Canada, and update Country
      const stateCountryId = state?.country?.id;

      if (stateCountryId === Country.UnitedStates.id) {
        countryCode = Country.UnitedStates.code;
      } else if (stateCountryId === Country.Canada.id) {
        countryCode = Country.Canada.code;
      } else {
        countryCode = '';
      }
    }
    return {
      countryCode,
      stateOrRegion,
      state,
      country,
    };
  }

  private setStateOrRegionValidators(country: Country, state: State) {
    const validator = getStateValidator(country, state);
    if (this.addressFormGroup.controls.stateOrRegion.hasValidator(validator)) {
      return;
    }
    this.addressFormGroup.controls.stateOrRegion.clearValidators();
    this.addressFormGroup.controls.stateOrRegion.addValidators(validator);
    this.addressFormGroup.controls.stateOrRegion.updateValueAndValidity();
  }

  private isCanada = () => this.addressFormGroup.value.country == Country.Canada.code;

  private getCountry(): Country {
    const code = this.addressFormGroup.value.country;
    return code ? this.countries.find((country) => country.code === code) : null;
  }

  private shouldClearStateOrRegion() {
    if (!this.addressFormGroup.value.stateOrRegion) {
      return false; //no value in stateOrRegion field, so work required
    }

    const stateCountryId = this.getState()?.country?.id;
    const currentCountry = this.getCountry();

    if (stateCountryId === Country.UnitedStates.id) {
      //if county set and is not US, clear
      //if country not set OR country is US, no clear
      return currentCountry && currentCountry.id !== Country.UnitedStates.id;
    }

    if (stateCountryId === Country.Canada.id) {
      return currentCountry?.id !== Country.Canada.id; //clear if country is not canada
    }

    return false; //Country not US or Canada and state is not US or Canada
  }
}

const stateRequired = NotEmptyValidator('State is required');
const provinceRequired = NotEmptyValidator('Province is required');
const regionRequired = NotEmptyValidator('Region is required');

const getStateByCode = (code: string, states: State[], provinces: State[]): State => {
  if (code) {
    return (
      states.find((s) => s.code === code) ?? provinces.find((province) => province.code === code)
    );
  }
};

const hasRegion = (country: Country, stateOrProvince: State): boolean => {
  if (country) {
    if (country.id === Country.UnitedStates.id || country.id === Country.Canada.id) {
      return false;
    }
    return true;
  }

  if (!stateOrProvince) {
    return false; //no country or stateRegion; treat as a state dropdown
  }
  //no country, but there is something in stateOrRegion
  const stateCountryId = stateOrProvince?.country?.id;
  if (stateCountryId === Country.UnitedStates.id || stateCountryId === Country.Canada.id)
    return false;

  return true;
};

const getStateValidator = (country: Country, state: State) => {
  if (hasRegion(country, state)) {
    return regionRequired;
  }

  if (country?.id === Country.Canada.id || state?.country?.id === Country.Canada.id)
    return provinceRequired;

  return stateRequired; //default is stateRequired. All cases where it's region handled in this.hasRegion();
};
